import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal, Text, LegacyStack } from "@shopify/polaris";

import { navigateWithShopInQuery } from "@vendor-app/utils/navigateWithShopInQuery";
import { adminRoutePrefix } from "@smartrr/shared/constants";

import PriceChangesGateway from "../../../../../../gateways/PriceChangesGateway";
import PriceChangesMapper from "../../../../../../mappers/PriceChangesMapper";
import { PriceChangeInterface } from "../../../../../../domain/PriceChangeInterface";

const PriceChangeRow = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #e1e3e5;

  &:last-child {
    border-bottom: none;
  }
`;

export function PriceChangesModal(): React.JSX.Element {
  const gateway = new PriceChangesGateway(new PriceChangesMapper());
  const productPreviewLimit = 3;

  const [priceChanges, setPriceChanges] = useState<PriceChangeInterface[]>([]);
  const [productCount, setProductCount] = useState<number>(0);
  const [totalAffectedSubscriptions, setTotalAffectedSubscriptions] = useState<number>(0);
  const [remainingPriceChangesCount, setRemainingPriceChangesCount] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(true);

  useEffect(() => {
    const fetchPriceChanges = async () => {
      try {
        const priceChanges = await gateway.fetchUnnotified();

        setPriceChanges(priceChanges);
        setProductCount(priceChanges.length);
        setTotalAffectedSubscriptions(
          priceChanges.reduce((sum, product) => sum + product.affectedSubscriptionsCount, 0)
        );
        setRemainingPriceChangesCount(Math.max(priceChanges.length - productPreviewLimit, 0));
        setIsModalOpen(priceChanges.length > 0);
      } catch (error) {
        console.error("Failed to fetch unnotified price changes:", error);
      }
    };

    fetchPriceChanges();
  }, []);

  useEffect(() => {
    const handleNotified = async () => {
      try {
        await gateway.notify(priceChanges.map(record => record.uuid));
      } catch (error) {
        console.error("Failed to mark price changes as notified:", error);
      }
    };

    if (priceChanges.length > 0) {
      handleNotified();
    }
  }, [priceChanges]);

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleReviewPriceUpdates = () => {
    navigateWithShopInQuery(`${adminRoutePrefix}/bulk-updates`, { tab: "price-changes" });
    closeModal();
  };

  const ModalContent = () => {
    return (
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        title="Price changes detected"
        primaryAction={{
          content: "Review price updates",
          onAction: handleReviewPriceUpdates,
        }}
        secondaryActions={[
          {
            content: "Dismiss",
            onAction: closeModal,
          },
        ]}
      >
        <Modal.Section>
          <span data-testid="price-changes-modal-info">
            <Text variant="bodyMd" as="span">
              You have{" "}
              <Text as="span" fontWeight="semibold">
                {productCount} {productCount === 1 ? "product" : "products"}
              </Text>{" "}
              with price changes that may affect{" "}
              <Text as="span" fontWeight="semibold">
                {totalAffectedSubscriptions} active{" "}
                {totalAffectedSubscriptions === 1 ? "subscription" : "subscriptions"}
              </Text>
              . Review and manage these updates from the Bulk Updates page.
            </Text>
          </span>
        </Modal.Section>

        <Modal.Section>
          <div>
            {priceChanges.slice(0, productPreviewLimit).map(product => (
              <PriceChangeRow key={product.uuid} data-testid="price-change-row">
                <LegacyStack spacing="baseTight" alignment="leading" distribution="equalSpacing" wrap={false}>
                  <LegacyStack.Item fill>
                    <div data-testid="price-change-product-name">
                      <Text variant="bodyMd" as="span" fontWeight="semibold" breakWord={true}>
                        {product.purchasableName}{" "}
                        {product.purchasableVariantName ? `- ${product.purchasableVariantName}` : ""}
                      </Text>
                    </div>
                  </LegacyStack.Item>

                  <div data-testid="price-change-old-price">
                    <Text variant="bodyMd" as="span" color="subdued">
                      {product.oldPrice}
                    </Text>
                  </div>

                  <div>
                    <Text variant="bodyMd" as="span" color="subdued">
                      →
                    </Text>
                  </div>

                  <div data-testid="price-change-new-price">
                    <Text variant="bodyMd" as="span" color="success" fontWeight="semibold">
                      {product.newPrice}
                    </Text>
                  </div>
                </LegacyStack>
              </PriceChangeRow>
            ))}
          </div>

          {!!remainingPriceChangesCount && (
            <div data-testid="price-changes-modal-remaining-count">
              <Text variant="bodyMd" as="span" color="subdued">
                +{remainingPriceChangesCount} more {remainingPriceChangesCount === 1 ? "product" : "products"}
              </Text>
            </div>
          )}
        </Modal.Section>
      </Modal>
    );
  };

  return isModalOpen && productCount > 0 ? <ModalContent /> : <React.Fragment></React.Fragment>;
}
